import type { Route42UserProfile } from '../auth/types';
import { getRuntimeConfig } from '../app/config/runtime-config';
import { extensionForFilename, maximumFileSizeBytes } from '../features/documents/document-upload-policy';

type MockUserMembership = {
  membership_id: string;
  customer_id: string;
  user_id: string | null;
  email: string;
  status: 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'REMOVED';
  role_ids: string[];
  effective_permissions: string[];
  facility_ids: string[];
  invited_by: string | null;
  invited_at: string | null;
  activated_at: string | null;
  updated_at: string | null;
};

type MockFacility = {
  facility_id: string;
  customer_id: string;
  facility_name: string;
  status: 'ACTIVE' | 'INACTIVE';
  location: string | null;
  created_at: string;
  updated_at: string;
};

type MockBilling = {
  customer_id: string;
  billing_account_id: string | null;
  provider: string;
  status: 'NOT_CONFIGURED' | 'PENDING' | 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'PAYMENT_ACTION_REQUIRED' | 'CANCELED';
  plan_id: string;
  billing_configured: boolean;
  checkout_enabled: boolean;
  can_start_checkout: boolean;
  can_manage_billing: boolean;
  currency: string | null;
  billing_interval: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  grace_period_end: string | null;
};


type MockTourStepId = `tour-step-${string}`;

type MockProductTourState = {
  schema_version: '1.0'; contract_id: string; tour_id: string; tour_version: number;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'SKIPPED' | 'COMPLETED'; current_step_id: MockTourStepId | null; highest_step_order: number;
  completed_step_ids: MockTourStepId[]; started_at: string | null; last_interaction_at: string | null; skipped_at: string | null; completed_at: string | null;
  replay_count: number; last_replay_started_at: string | null; updated_at: string | null; revision: number; has_completed: boolean; has_skipped: boolean;
};

const mockTourStepOrder = new Map<MockTourStepId, number>(
  Array.from({ length: 10 }, (_, index) => [`tour-step-${String(index + 1).padStart(2, '0')}` as MockTourStepId, index + 1]),
);
const mockRequiredTourSteps = new Set<MockTourStepId>(['tour-step-01', 'tour-step-02', 'tour-step-04', 'tour-step-05', 'tour-step-06', 'tour-step-08', 'tour-step-10']);

function isMockTourStepId(value: string): value is MockTourStepId {
  return /^tour-step-\d{2}$/.test(value);
}
let mockProductTourState: MockProductTourState = {
  schema_version: '1.0', contract_id: 'route42-core-v1', tour_id: 'route42-core', tour_version: 1, status: 'NOT_STARTED', current_step_id: null, highest_step_order: 0, completed_step_ids: [], started_at: null, last_interaction_at: null, skipped_at: null, completed_at: null, replay_count: 0, last_replay_started_at: null, updated_at: null, revision: 0, has_completed: false, has_skipped: false,
};

type MockMachine = {
  machine_id: string;
  machine_name: string;
  facility_id: string | null;
  asset_id: string | null;
  machine_type: string | null;
  manufacturer: string | null;
  model: string | null;
  location: string | null;
  production_area: string | null;
  production_line: string | null;
  status: 'ACTIVE' | 'ARCHIVED';
  version?: number;
  updated_at: string;
  status_changed_at?: string | null;
  archived_at?: string | null;
  restored_at?: string | null;
};


type MockDocument = {
  document_id: string;
  machine_id: string;
  processing_run_id: string | null;
  original_filename: string;
  file_type: string;
  file_size_bytes: number;
  upload_status: string;
  processing_status: string;
  security_status: string;
  created_at: string;
  uploaded_at: string | null;
  original_storage_status: string | null;
  original_location: { bucket: string; key: string } | null;
  content_fingerprint: string | null;
  duplicate_status: string | null;
  duplicate_of_document_id: string | null;
  stored_at: string | null;
  question_ready: boolean;
  upload_expires_at?: string | null;
  upload_expired?: boolean;
  abandonment_reason?: string | null;
  lifecycle_status?: 'ACTIVE' | 'ARCHIVED' | 'REMOVED';
  version?: number;
  removed_reason?: string | null;
  removed_at?: string | null;
  removed_by?: string | null;
  removal_reconciliation_status?: string | null;
  removal_evidence_revision?: number | null;
  machine_evidence_revision?: number | null;
  machine_evidence_reconciliation_status?: string | null;
  machine_evidence_reconciliation_stage?: string | null;
  machine_evidence_reconciliation_error_code?: string | null;
  machine_evidence_reconciliation_attempt?: number | null;
  machine_evidence_reconciliation_retryable?: boolean | null;
  machine_evidence_reconciliation_failed_at?: string | null;
  machine_evidence_reconciliation_source_document_id?: string | null;
  machine_evidence_reconciliation_retry_requested_at?: string | null;
  review?: {
    type: 'MACHINE_ASSOCIATION' | 'CLASSIFICATION';
    status: 'REQUIRED' | 'COMPLETED';
    reason_code: string;
    summary: string;
    selected_machine_name?: string | null;
    assessment?: string | null;
    match_confidence?: number | null;
    identity_strength?: string | null;
    multiple_machines_detected?: boolean;
    conflicts: Array<{ field: string; evidence_type?: string; selected_value?: string; document_value?: string; source_reference?: { page?: number } | null }>;
    available_decisions: Array<'CONFIRM' | 'REJECT'>;
    decision?: 'CONFIRM' | 'REJECT' | null;
    reviewed_at?: string | null;
  } | null;
};


type MockConversation = {
  conversation_id: string;
  customer_id: string;
  machine_id: string;
  user_id: string;
  conversation_type: 'GENERAL_MACHINE_CONVERSATION' | 'INVESTIGATION_CONVERSATION';
  investigation_id?: string | null;
  title: string | null;
  status: 'ACTIVE';
  turn_count: number;
  created_at: string;
  updated_at: string;
};



type MockInvestigation = {
  investigation_id: string;
  customer_id: string;
  machine_id: string;
  title: string;
  objective: string;
  owner_user_id: string;
  status: 'OPEN' | 'PAUSED' | 'CLOSED' | 'ARCHIVED';
  participant_user_ids: string[];
  participant_roles: Record<string, 'OWNER' | 'EDITOR' | 'CONTRIBUTOR' | 'VIEWER'>;
  source_conversation_id: string | null;
  conversation_ids: string[];
  created_at: string;
  updated_at: string;
  closure?: { outcome: string; what_happened: string; what_learned: string; what_was_done: string; what_happened_afterward: string; follow_up: string; closed_by_user_id: string; closed_at: string } | null;
};

type MockInvestigationMemory = {
  investigation_id: string; customer_id: string; machine_id: string;
  working_hypotheses: Array<Record<string, unknown>>; findings: Array<Record<string, unknown>>;
  conflicting_evidence: Array<Record<string, unknown>>; rejected_hypotheses: Array<Record<string, unknown>>;
  open_questions: string[]; notes: Array<Record<string, unknown>>; conclusions: Array<Record<string, unknown>>;
  current_filters: Record<string, unknown>; updated_at: string;
};

type MockPinnedEvidence = { evidence_id: string; artifact_type: string; artifact_id: string; pinned_by_user_id: string; pinned_at: string; note: string | null };
type MockInvestigationContent = { content_id: string; content_type: string; text: string; created_by_user_id: string; created_at: string; supporting_evidence_ids: string[] };
type MockInvestigationActivity = { activity_id: string; investigation_id: string; user_id: string; activity_type: string; created_at: string; payload: Record<string, unknown> };
type MockMachineKnowledgeRecord = {
  record_id: string; knowledge_id: string; knowledge_type: string; statement: string; investigation_id: string;
  closure_timestamp: string; confirmed_by_user_id: string; reasoning_state_version: number | null;
  supporting_evidence: Array<{ artifact_type: string; artifact_id: string; document_id?: string | null; source_locator?: Record<string, unknown> | null }>;
  closure_source: { closure_outcome: string; source_kind: string; source_text: string; source_id: string | null };
  authority: string; structuring_method: string; status: 'ACTIVE' | 'REVIEW_REQUIRED' | 'SUPERSEDED' | 'RETRACTED'; version: number;
  supersedes_record_id: string | null; superseded_by_record_id: string | null; lifecycle_changed_at: string | null; lifecycle_reason: string | null;
};
type MockNotification = { notification_id: string; notification_type: 'INVESTIGATION_OPENED' | 'INVESTIGATION_CLOSED' | 'INVESTIGATION_REOPENED'; title: string; message: string; machine_id: string; machine_name: string; investigation_id: string; investigation_title: string; created_at: string; read_at: string | null };

type MockTurn = {
  turn_id: string;
  conversation_id: string;
  customer_id: string;
  machine_id: string;
  user_id: string;
  question: string;
  answer: {
    answer_text: string;
    citations: Array<{ citation_id: string; artifact_type: string; artifact_id: string; document_id: string | null; label: string | null; source_locator: Record<string, unknown> | null; excerpt?: string | null }>;
    uncertainty: string | null;
    conflicting_evidence: boolean;
    retrieved_artifacts: Array<Record<string, unknown>>;
  } | null;
  created_at: string;
  interaction_type?: 'ACE_QUESTION' | 'TEAM_UPDATE';
  actor_label?: string | null;
};

const conversations: MockConversation[] = [];
const conversationTurns = new Map<string, MockTurn[]>();
const conversationScopes = new Map<string, string[]>();
const investigations: MockInvestigation[] = [];
const investigationMemories = new Map<string, MockInvestigationMemory>();
const investigationEvidence = new Map<string, MockPinnedEvidence[]>();
const investigationContent = new Map<string, MockInvestigationContent[]>();
const investigationActivity = new Map<string, MockInvestigationActivity[]>();
const investigationMachineKnowledge = new Map<string, MockMachineKnowledgeRecord[]>();
const mockNotifications: MockNotification[] = [];

const MOCK_BILLING_STORAGE_KEY = 'route42:mock-billing-state';
const MOCK_BILLING_ACTIVATION_AT_KEY = 'route42:mock-billing-activation-at';

const defaultMockBilling: MockBilling = {
  customer_id: 'cust_local_route42',
  billing_account_id: null,
  provider: 'stripe',
  status: 'NOT_CONFIGURED',
  plan_id: 'mvp',
  billing_configured: false,
  checkout_enabled: true,
  can_start_checkout: true,
  can_manage_billing: false,
  currency: null,
  billing_interval: null,
  current_period_end: null,
  cancel_at_period_end: false,
  grace_period_end: null,
};

function readMockBilling(): MockBilling {
  try {
    const persisted = window.sessionStorage.getItem(MOCK_BILLING_STORAGE_KEY);
    if (!persisted) return { ...defaultMockBilling };
    return { ...defaultMockBilling, ...(JSON.parse(persisted) as Partial<MockBilling>) };
  } catch {
    return { ...defaultMockBilling };
  }
}

function persistMockBilling(value: MockBilling): void {
  try {
    window.sessionStorage.setItem(MOCK_BILLING_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Local mock persistence is best-effort only.
  }
}

function resolvePendingMockBilling(): void {
  if (mockBilling.status !== 'PENDING') return;
  try {
    const activateAt = Number(window.sessionStorage.getItem(MOCK_BILLING_ACTIVATION_AT_KEY));
    if (!Number.isFinite(activateAt) || Date.now() < activateAt) return;
    mockBilling = {
      ...mockBilling,
      status: 'ACTIVE',
      current_period_end: '2026-09-30T23:59:59Z',
    };
    persistMockBilling(mockBilling);
    window.sessionStorage.removeItem(MOCK_BILLING_ACTIVATION_AT_KEY);
  } catch {
    // The in-memory mock state remains usable if storage is unavailable.
  }
}

let mockBilling: MockBilling = readMockBilling();

const facilities: MockFacility[] = [
  { facility_id: 'fac_plant_01', customer_id: 'cust_local_route42', facility_name: 'Plant 01', status: 'ACTIVE', location: 'Atlanta, GA', created_at: '2026-08-18T13:00:00Z', updated_at: '2026-08-18T13:00:00Z' },
  { facility_id: 'fac_plant_02', customer_id: 'cust_local_route42', facility_name: 'Plant 02', status: 'ACTIVE', location: 'Dallas, TX', created_at: '2026-08-18T13:05:00Z', updated_at: '2026-08-18T13:05:00Z' },
];

const machines: MockMachine[] = [
  {
    machine_id: 'mach_nhvf', machine_name: 'NHVf Pump', facility_id: 'fac_plant_01', asset_id: 'CV-017',
    machine_type: 'Centrifugal Pump', manufacturer: 'Hydro-Vacuum S.A.', model: 'NHVf Series', location: 'Plant 01',
    production_area: null, production_line: null, status: 'ACTIVE', updated_at: '2026-08-21T14:42:00Z',
  },
  {
    machine_id: 'mach_compressor_04', machine_name: 'Compressor 04', facility_id: 'fac_plant_01', asset_id: 'COMP-004',
    machine_type: 'Rotary Screw Compressor', manufacturer: null, model: null, location: 'Plant 01',
    production_area: null, production_line: null, status: 'ACTIVE', updated_at: '2026-08-20T18:12:00Z',
  },
  {
    machine_id: 'mach_boiler_feed_02', machine_name: 'Boiler Feed Pump 02', facility_id: 'fac_plant_02', asset_id: 'BFP-002',
    machine_type: 'Centrifugal Pump', manufacturer: null, model: null, location: 'Plant 02',
    production_area: null, production_line: null, status: 'ACTIVE', updated_at: '2026-08-21T12:05:00Z',
  },
];

const documents: MockDocument[] = [
  {
    document_id: 'doc_nhvf_manual', machine_id: 'mach_nhvf', processing_run_id: null, original_filename: 'NHVf OEM Manual.pdf',
    file_type: 'PDF', file_size_bytes: 2480000, upload_status: 'UPLOADED', processing_status: 'READY_FOR_CROSS_DOCUMENT_ANALYSIS',
    security_status: 'PASSED', created_at: '2026-08-20T12:00:00Z', uploaded_at: '2026-08-20T12:01:00Z',
    original_storage_status: 'STORED', original_location: null, content_fingerprint: 'mock-fingerprint', duplicate_status: 'UNIQUE',
    duplicate_of_document_id: null, stored_at: '2026-08-20T12:02:00Z', question_ready: true, lifecycle_status: 'ACTIVE', version: 1, review: null, upload_expires_at: null, upload_expired: false, abandonment_reason: null,
  },
];

export const mockProfile: Route42UserProfile = {
  user: { user_id: 'user_local_route42', email: 'route42.local@example.com', email_verified: true, username: 'route42.local@example.com' },
  memberships: [{
    membership_id: 'mbr_local_route42', customer_id: 'cust_local_route42', customer_name: 'Route42 Demo Manufacturing',
    customer_status: 'ACTIVE', membership_status: 'ACTIVE', access_available: true, role_ids: ['customer_admin'],
    effective_permissions: ['organization:read', 'facility:read', 'facility:create', 'machine:read', 'machine:create', 'machine:update', 'machine:archive', 'machine:restore', 'machine:delete', 'documents:read', 'documents:upload', 'documents:review', 'documents:remove', 'documents:restore', 'documents:delete', 'conversation:read', 'conversation:create', 'question:ask', 'investigation:read', 'investigation:create', 'investigation:update', 'investigation:contribute', 'investigation:manage_participants', 'user:read', 'user:invite', 'user:assign_role', 'user:suspend', 'user:reactivate', 'user:remove', 'billing:read', 'billing:manage'],
    entitlements: { plan_id: 'mvp', display_name: 'MVP', features: ['machine_management', 'document_upload', 'question_answering', 'investigations', 'user_administration'], limits: { max_users: 25, max_machines: 50 } }, facility_ids: [],
  }],
  membership_count: 1,
};

const mockUsers: MockUserMembership[] = [
  {
    membership_id: 'mbr_local_route42', customer_id: 'cust_local_route42', user_id: 'user_local_route42', email: 'route42.local@example.com', status: 'ACTIVE',
    role_ids: ['customer_admin'], effective_permissions: [...mockProfile.memberships[0].effective_permissions], facility_ids: [], invited_by: 'route42-platform',
    invited_at: '2026-08-18T14:00:00Z', activated_at: '2026-08-18T14:15:00Z', updated_at: '2026-08-18T14:15:00Z',
  },
  {
    membership_id: 'mbr_local_manager', customer_id: 'cust_local_route42', user_id: 'user_local_manager', email: 'manager@example.com', status: 'ACTIVE',
    role_ids: ['maintenance_manager'], effective_permissions: ['organization:read', 'facility:read', 'machine:read', 'machine:create', 'machine:update', 'machine:archive', 'machine:restore', 'documents:read', 'documents:upload', 'documents:review', 'documents:remove', 'documents:restore', 'conversation:read', 'conversation:create', 'artifact:read', 'question:ask', 'investigation:read', 'investigation:create', 'investigation:update', 'investigation:contribute', 'investigation:manage_participants'], facility_ids: [], invited_by: 'user_local_route42',
    invited_at: '2026-08-19T10:00:00Z', activated_at: '2026-08-19T10:20:00Z', updated_at: '2026-08-19T10:20:00Z',
  },
  {
    membership_id: 'mbr_local_invited', customer_id: 'cust_local_route42', user_id: null, email: 'technician.pending@example.com', status: 'INVITED',
    role_ids: ['technician'], effective_permissions: ['organization:read', 'facility:read', 'machine:read', 'documents:read', 'documents:upload', 'documents:review', 'conversation:read', 'conversation:create', 'artifact:read', 'question:ask', 'investigation:read', 'investigation:create', 'investigation:update', 'investigation:contribute'], facility_ids: [], invited_by: 'user_local_route42',
    invited_at: '2026-08-22T11:00:00Z', activated_at: null, updated_at: '2026-08-22T11:00:00Z',
  },
];

function mockPermissionsForRoles(roleIds: string[]): string[] {
  if (roleIds.includes('customer_admin')) return [...mockProfile.memberships[0].effective_permissions];
  const permissions = new Set<string>();
  if (roleIds.includes('maintenance_manager')) ['organization:read','facility:read','machine:read','machine:create','machine:update','machine:archive','machine:restore','documents:read','documents:upload','documents:review','documents:remove','documents:restore','conversation:read','conversation:create','artifact:read','question:ask','investigation:read','investigation:create','investigation:update','investigation:contribute','investigation:manage_participants'].forEach((item) => permissions.add(item));
  if (roleIds.includes('technician')) ['organization:read','facility:read','machine:read','documents:read','documents:upload','documents:review','conversation:read','conversation:create','artifact:read','question:ask','investigation:read','investigation:create','investigation:update','investigation:contribute'].forEach((item) => permissions.add(item));
  if (roleIds.includes('viewer')) ['organization:read','facility:read','machine:read','documents:read','conversation:read','artifact:read','investigation:read'].forEach((item) => permissions.add(item));
  return [...permissions].sort();
}

export async function mockApiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  await new Promise((resolve) => window.setTimeout(resolve, 120));
  const url = new URL(path, window.location.origin);
  const method = init?.method ?? 'GET';

  if (url.pathname === '/v1/auth/me' && method === 'GET') return structuredClone(mockProfile) as T;

  if (url.pathname === '/v1/notifications' && method === 'GET') {
    const requestedLimit = Number.parseInt(url.searchParams.get('limit') ?? '20', 10);
    const limit = Number.isFinite(requestedLimit) && requestedLimit > 0 ? Math.min(requestedLimit, 50) : 20;
    const items = [...mockNotifications].sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, limit);
    return { schema_version: '1.0', notifications: structuredClone(items), count: items.length, unread_count: items.filter((item) => !item.read_at).length, request_id: 'req_local_notifications' } as T;
  }
  if (url.pathname === '/v1/notifications/read-all' && method === 'POST') {
    const now = new Date().toISOString();
    let updated = 0;
    for (const item of mockNotifications) if (!item.read_at) { item.read_at = now; updated += 1; }
    return { schema_version: '1.0', read: true, updated_count: updated, request_id: 'req_local_notifications_read_all' } as T;
  }
  const notificationRead = url.pathname.match(/^\/v1\/notifications\/([^/]+)$/);
  if (notificationRead && method === 'PATCH') {
    const id = decodeURIComponent(notificationRead[1]);
    const item = mockNotifications.find((candidate) => candidate.notification_id === id);
    if (item && !item.read_at) item.read_at = new Date().toISOString();
    return { schema_version: '1.0', notification_id: id, read: true, changed: Boolean(item), request_id: 'req_local_notification_read' } as T;
  }

  if (url.pathname === '/v1/ace/messages' && method === 'POST') {
    const body = JSON.parse(String(init?.body ?? '{}')) as { question?: string; context_hint?: { route?: { id?: string } | null } };
    const question = String(body.question ?? '').trim();
    const lower = question.toLocaleLowerCase();
    const routeId = body.context_hint?.route?.id ?? null;
    const machineSpecific = /(why|cause|failing|failure|diagnos|evidence for|what happened)/i.test(question) && /(pump|machine|conveyor|compressor|bearing|seal)/i.test(question);
    if (machineSpecific) {
      return { schema_version: '1.0', request_id: 'req_local_ace_machine', mode: 'MACHINE_INTELLIGENCE', intent: null, grounding_status: 'NO_AUTHORITATIVE_GUIDANCE', routing_reason: 'MACHINE_ANALYSIS_REQUEST', knowledge_basis: 'MACHINE_EVIDENCE_REQUIRED', answer: { text: 'That question is about the machine itself. I can move you into Machine Intelligence so Ace can answer from grounded machine evidence.', steps: [], why_it_matters: null, next_action: 'Continue in Machine Intelligence.' }, sources: [], delegation: { target: 'MACHINE_INTELLIGENCE', recommended_route_id: 'new-conversation', recommended_action: 'question.ask', machine_id: 'mach_nhvf', conversation_id: null }, recommendation: null, fallback_code: null, corpus_digest: 'sha256:' + '0'.repeat(64) } as T;
    }
    const whatNext = lower.includes('what should i do next');
    const whatHere = lower.includes('what can i do here') || lower.includes('this page');
    const page = routeId ? routeId.replaceAll('-', ' ') : 'Route42';
    const mockRecommendationByRoute: Record<string, { id: string; action_id: string; route_id: string; label: string; summary: string; why_it_matters: string; priority: number; category: string }> = {
      'create-machine': { id: 'finish-current-machine-setup', action_id: 'machine.create', route_id: 'create-machine', label: 'Finish creating this machine', summary: 'You are already in machine setup. Add the identifying details you know now and create the machine.', why_it_matters: 'Creating the machine gives everything you add or learn one clear place to belong.', priority: 96, category: 'FOUNDATION' },
      'add-documents': { id: 'finish-adding-machine-information', action_id: 'document.upload', route_id: 'add-documents', label: 'Add the information you have', summary: 'Start with the useful machine information you have now; you do not need a perfect document set.', why_it_matters: 'Grounded sources strengthen later questions, Investigations, and Machine Memory.', priority: 96, category: 'FOUNDATION' },
      'new-conversation': { id: 'start-focused-conversation', action_id: 'question.ask', route_id: 'new-conversation', label: 'Ask one focused question', summary: 'Start with the specific machine question you want answered now, then build on the answer with follow-ups.', why_it_matters: 'A focused question gives Route42 a clear target and creates a useful line of inquiry.', priority: 96, category: 'FIRST_VALUE' },
      conversation: { id: 'continue-active-conversation', action_id: 'question.ask', route_id: 'conversation', label: 'Ask a focused follow-up', summary: 'Keep developing the current line of inquiry before moving it into a more structured workflow.', why_it_matters: 'A few focused exchanges can clarify whether the issue is resolved or deserves an Investigation.', priority: 94, category: 'CONTINUE_WORK' },
      investigation: { id: 'continue-investigation-objective', action_id: 'investigation.contribute', route_id: 'investigation', label: 'Keep working the investigation', summary: 'Use the current Investigation to resolve what is still unknown and strengthen the assessment.', why_it_matters: 'Keeping evidence, questions, findings, and conclusions in one workflow makes the outcome easier to act on.', priority: 92, category: 'CONTINUE_WORK' },
      documents: { id: 'choose-document-from-library', action_id: 'document.view', route_id: 'documents', label: 'Choose a document', summary: 'Open an existing document when you want to review what Route42 learned, or add information when something important is missing.', why_it_matters: 'Starting from the right source keeps your next action tied to the machine information you actually want to use.', priority: 90, category: 'CONTINUE_WORK' },
      conversations: { id: 'choose-conversation-from-history', action_id: 'conversation.view', route_id: 'conversations', label: 'Choose a conversation', summary: 'Resume an existing line of inquiry when it is still relevant, or start a new one for a different question.', why_it_matters: 'Reusing the right Conversation preserves context without mixing unrelated questions.', priority: 90, category: 'CONTINUE_WORK' },
      investigations: { id: 'choose-open-investigation', action_id: 'investigation.view', route_id: 'investigations', label: 'Continue an investigation', summary: 'Open the Investigation that still needs work before starting another disconnected one.', why_it_matters: 'Finishing active work keeps evidence and follow-through focused on a clear objective.', priority: 90, category: 'CONTINUE_WORK' },
      machines: { id: 'choose-machine-from-directory', action_id: 'machine.switch', route_id: 'machines', label: 'Choose a machine', summary: 'Open the machine you want to continue working with, or create a new one if it is not listed.', why_it_matters: 'The right machine keeps documents, questions, Investigations, and Memory attached to the correct equipment.', priority: 90, category: 'CONTINUE_WORK' },
      document: { id: 'ask-about-current-document', action_id: 'question.ask', route_id: 'new-conversation', label: 'Ask about this document', summary: 'Use this source as the grounding for a focused machine question when it is ready.', why_it_matters: 'Document-scoped questions connect your next inquiry directly to the information you are reviewing.', priority: 89, category: 'FIRST_VALUE' },
      'machine-memory': { id: 'use-machine-memory-as-briefing', action_id: 'memory.view', route_id: 'machine-memory', label: 'Use Machine Memory as a briefing', summary: 'Review what Route42 currently understands before deciding what deserves deeper follow-up.', why_it_matters: 'Machine Memory helps you orient around the strongest knowledge, gaps, and conflicts without starting from scratch.', priority: 85, category: 'STRENGTHEN' },
    };
    const recommendation = whatNext ? (mockRecommendationByRoute[routeId ?? ''] ?? { id: 'ask-first-grounded-question', action_id: 'question.ask', route_id: 'new-conversation', label: 'Ask your first grounded question', summary: 'You have useful machine information ready, so the next high-value step is to ask one focused question.', why_it_matters: 'A focused question turns the information Route42 has learned into an answer your team can use.', priority: 70, category: 'FIRST_VALUE' }) : null;
    return { schema_version: '1.0', request_id: 'req_local_ace_guidance', mode: 'APPLICATION_GUIDANCE', intent: whatNext ? 'WHAT_NEXT' : whatHere ? 'WHAT_IS_THIS' : 'HOW_DO_I', grounding_status: 'GROUNDED', routing_reason: whatNext ? 'DETERMINISTIC_NEXT_BEST_ACTION' : 'GROUNDED_PRODUCT_KNOWLEDGE', knowledge_basis: whatNext ? 'ROUTE42_APPLICATION_CONTEXT' : 'ROUTE42_PRODUCT_HELP', answer: { text: recommendation?.summary ?? (whatHere ? `You are in ${page}. This area helps you complete the work associated with this part of Route42.` : 'I can walk you through that in a few clear steps.'), steps: [], why_it_matters: recommendation?.why_it_matters ?? 'Keeping the next action clear helps you reach useful machine intelligence without unnecessary setup.', next_action: recommendation?.label ?? 'Use the primary action on this page, or ask me about a specific task.' }, sources: whatNext ? [] : [{ article_id: 'route42-glossary', title: 'Route42 glossary', content_version: 1, last_reviewed: '2026-09-01' }], delegation: null, recommendation, fallback_code: null, corpus_digest: 'sha256:' + '0'.repeat(64) } as T;
  }



  const productTourState = url.pathname.match(/^\/v1\/product-guidance\/tours\/(route42-core-v1)\/state$/);
  if (productTourState && method === 'GET') return structuredClone(mockProductTourState) as T;
  if (productTourState && method === 'PUT') {
    const body = JSON.parse(String(init?.body ?? '{}')) as { action?: string; step_id?: string };
    const action = String(body.action ?? '').toUpperCase();
    const now = new Date().toISOString();
    if (action === 'START') {
      if (mockProductTourState.status === 'NOT_STARTED') {
        mockProductTourState = { ...mockProductTourState, status: 'IN_PROGRESS', current_step_id: 'tour-step-01', started_at: mockProductTourState.started_at ?? now };
      }
    } else if (action === 'STEP_COMPLETED') {
      const candidateStepId = String(body.step_id ?? '');
      const stepId = isMockTourStepId(candidateStepId) ? candidateStepId : null;
      const order = stepId ? mockTourStepOrder.get(stepId) : undefined;
      if (mockProductTourState.status === 'IN_PROGRESS' && stepId && order) {
        const completed = new Set(mockProductTourState.completed_step_ids); completed.add(stepId);
        mockProductTourState = { ...mockProductTourState, current_step_id: stepId, completed_step_ids: [...completed].sort((a, b) => (mockTourStepOrder.get(a) ?? 0) - (mockTourStepOrder.get(b) ?? 0)), highest_step_order: Math.max(mockProductTourState.highest_step_order, order) };
      }
    } else if (action === 'SKIP') {
      if (mockProductTourState.status === 'NOT_STARTED' || mockProductTourState.status === 'IN_PROGRESS') {
        mockProductTourState = { ...mockProductTourState, status: 'SKIPPED', current_step_id: null, skipped_at: mockProductTourState.skipped_at ?? now, has_skipped: true };
      }
    } else if (action === 'REPLAY') {
      if (mockProductTourState.status === 'SKIPPED' || mockProductTourState.status === 'COMPLETED') {
        mockProductTourState = { ...mockProductTourState, status: 'IN_PROGRESS', current_step_id: 'tour-step-01', highest_step_order: 0, completed_step_ids: [], replay_count: mockProductTourState.replay_count + 1, last_replay_started_at: now };
      }
    } else if (action === 'COMPLETE') {
      if (mockProductTourState.status === 'IN_PROGRESS' && [...mockRequiredTourSteps].every((step) => mockProductTourState.completed_step_ids.includes(step))) {
        mockProductTourState = { ...mockProductTourState, status: 'COMPLETED', current_step_id: 'tour-step-10', highest_step_order: 10, completed_at: mockProductTourState.completed_at ?? now, has_completed: true };
      }
    }
    mockProductTourState = { ...mockProductTourState, last_interaction_at: now, updated_at: now, revision: mockProductTourState.revision + 1 };
    return structuredClone(mockProductTourState) as T;
  }

  if (url.pathname === '/v1/product-guidance/events' && method === 'POST') {
    return { schema_version: '1.0', accepted: true, request_id: 'req_local_guidance_event' } as T;
  }


  const customerBilling = url.pathname.match(/^\/v1\/customers\/([^/]+)\/billing$/);
  if (customerBilling && method === 'GET') {
    resolvePendingMockBilling();
    return { ...structuredClone(mockBilling), customer_id: decodeURIComponent(customerBilling[1]), request_id: 'req_local_billing' } as T;
  }

  const customerCheckout = url.pathname.match(/^\/v1\/customers\/([^/]+)\/billing\/checkout-session$/);
  if (customerCheckout && method === 'POST') {
    const customerId = decodeURIComponent(customerCheckout[1]);
    mockBilling = {
      ...mockBilling,
      customer_id: customerId,
      billing_account_id: mockBilling.billing_account_id ?? 'bill_local_route42',
      status: 'PENDING',
      billing_configured: true,
      can_start_checkout: false,
      can_manage_billing: true,
      currency: 'usd',
      billing_interval: 'month',
    };
    persistMockBilling(mockBilling);
    try {
      window.sessionStorage.setItem(MOCK_BILLING_ACTIVATION_AT_KEY, String(Date.now() + 50));
    } catch {
      // Local mock persistence is best-effort only.
    }
    window.setTimeout(() => {
      mockBilling = {
        ...mockBilling,
        status: 'ACTIVE',
        current_period_end: '2026-09-30T23:59:59Z',
      };
      persistMockBilling(mockBilling);
      try {
        window.sessionStorage.removeItem(MOCK_BILLING_ACTIVATION_AT_KEY);
      } catch {
        // Local mock persistence is best-effort only.
      }
    }, 50);
    return {
      billing: structuredClone(mockBilling),
      checkout_session: {
        provider: 'stripe',
        session_id: 'cs_test_route42_local',
        url: `${window.location.origin}/settings/billing?billing_checkout=success`,
      },
      request_id: 'req_local_billing_checkout',
    } as T;
  }

  const customerPortal = url.pathname.match(/^\/v1\/customers\/([^/]+)\/billing\/portal-session$/);
  if (customerPortal && method === 'POST') {
    return {
      portal_session: {
        provider: 'stripe',
        session_id: 'bps_local_route42',
        url: `${window.location.origin}/settings/billing?billing_portal=returned`,
      },
      request_id: 'req_local_billing_portal',
    } as T;
  }

  const customerFacilities = url.pathname.match(/^\/v1\/customers\/([^/]+)\/facilities$/);
  if (customerFacilities && method === 'GET') {
    return {
      customer: { customer_id: decodeURIComponent(customerFacilities[1]), customer_name: 'Route42 Demo Manufacturing', status: 'ACTIVE', entitlements: { plan_id: 'mvp', plan_version: 1, display_name: 'MVP', features: ['document_upload', 'investigations', 'machine_management', 'question_answering', 'user_administration'], limits: { max_machines: 50, max_users: 25 } } },
      facilities: structuredClone(facilities), facility_count: facilities.length, request_id: 'req_local_facilities',
    } as T;
  }
  if (customerFacilities && method === 'POST') {
    const body = JSON.parse(String(init?.body ?? '{}')) as { facility_name?: string; location?: string | null };
    const now = new Date().toISOString();
    const facility: MockFacility = {
      facility_id: `fac_local_${Date.now()}`, customer_id: decodeURIComponent(customerFacilities[1]), facility_name: String(body.facility_name ?? '').trim(), status: 'ACTIVE', location: body.location ? String(body.location).trim() : null, created_at: now, updated_at: now,
    };
    facilities.push(facility);
    return { facility: structuredClone(facility), request_id: 'req_local_facility_create' } as T;
  }

  const customerUsers = url.pathname.match(/^\/v1\/customers\/([^/]+)\/users$/);
  if (customerUsers && method === 'GET') {
    return {
      customer: { customer_id: decodeURIComponent(customerUsers[1]), customer_name: 'Route42 Demo Manufacturing', status: 'ACTIVE', entitlements: { plan_id: 'mvp', plan_version: 1, display_name: 'MVP', features: ['document_upload', 'investigations', 'machine_management', 'question_answering', 'user_administration'], limits: { max_machines: 50, max_users: 25 } } },
      users: structuredClone(mockUsers), user_count: mockUsers.length, request_id: 'req_local_users',
    } as T;
  }

  const customerInvitations = url.pathname.match(/^\/v1\/customers\/([^/]+)\/users\/invitations$/);
  if (customerInvitations && method === 'POST') {
    const body = JSON.parse(String(init?.body ?? '{}')) as { email?: string; role_ids?: string[]; facility_ids?: string[] };
    const email = String(body.email ?? '').trim().toLocaleLowerCase();
    const existing = mockUsers.find((item) => item.email.toLocaleLowerCase() === email && item.status !== 'REMOVED');
    const now = new Date().toISOString();
    const membership = existing ?? { membership_id: `mbr_local_${Date.now()}`, customer_id: 'cust_local_route42', user_id: null, email, status: 'INVITED' as const, role_ids: body.role_ids?.length ? [...body.role_ids] : ['viewer'], effective_permissions: mockPermissionsForRoles(body.role_ids?.length ? body.role_ids : ['viewer']), facility_ids: Array.isArray(body.facility_ids) ? [...body.facility_ids] : [], invited_by: 'user_local_route42', invited_at: now, activated_at: null, updated_at: now };
    if (!existing) mockUsers.push(membership);
    return { membership: structuredClone(membership), identity: { status: 'INVITATION_REQUESTED', invitation_requested: true, cognito_username: email, cognito_user_status: 'FORCE_CHANGE_PASSWORD' }, idempotent: Boolean(existing), next_step: 'MEMBERSHIP_ACTIVATION', request_id: 'req_local_invite' } as T;
  }

  const userAction = url.pathname.match(/^\/v1\/customers\/([^/]+)\/users\/([^/]+)(?:\/(roles|resend-invitation|suspend|reactivate))?$/);
  if (userAction) {
    const membershipId = decodeURIComponent(userAction[2]);
    const action = userAction[3] ?? null;
    const membership = mockUsers.find((item) => item.membership_id === membershipId);
    if (!membership) throw new Error('Mock membership not found.');
    const now = new Date().toISOString();
    if (method === 'PUT' && action === 'roles') {
      const body = JSON.parse(String(init?.body ?? '{}')) as { role_ids?: string[] };
      membership.role_ids = body.role_ids?.length ? [...body.role_ids] : membership.role_ids;
      membership.effective_permissions = mockPermissionsForRoles(membership.role_ids);
      membership.updated_at = now;
      return { membership: structuredClone(membership), request_id: 'req_local_roles' } as T;
    }
    if (method === 'POST' && action === 'resend-invitation') return { membership: structuredClone(membership), identity: { status: 'INVITATION_REQUESTED', invitation_requested: true, cognito_username: membership.email, cognito_user_status: 'FORCE_CHANGE_PASSWORD' }, idempotent: false, next_step: 'MEMBERSHIP_ACTIVATION', request_id: 'req_local_resend' } as T;
    if (method === 'POST' && action === 'suspend') membership.status = 'SUSPENDED';
    if (method === 'POST' && action === 'reactivate') membership.status = 'ACTIVE';
    if (method === 'DELETE' && !action) membership.status = 'REMOVED';
    membership.updated_at = now;
    return { membership: structuredClone(membership), idempotent: false, request_id: 'req_local_user_action' } as T;
  }

  if (url.pathname === '/v1/machines' && method === 'GET') {
    const search = (url.searchParams.get('search') ?? '').trim().toLocaleLowerCase();
    const status = (url.searchParams.get('status') ?? 'ACTIVE').toLocaleUpperCase();
    const filtered = machines.filter((machine) => machine.status === status && (search ? machine.machine_name.toLocaleLowerCase().startsWith(search) : true));
    return { machines: structuredClone(filtered), next_cursor: null, request_id: 'req_local' } as T;
  }

  const machineActivity = url.pathname.match(/^\/v1\/machines\/([^/]+)\/activity$/);
  if (machineActivity && method === 'GET') {
    const machineId = decodeURIComponent(machineActivity[1]);
    const machine = machines.find((item) => item.machine_id === machineId);
    if (!machine) throw new Error('Mock machine not found.');
    const values = [
      { activity_id: 'activity_memory_4', occurred_at: '2026-08-23T12:00:00Z', category: 'INTELLIGENCE', event_type: 'MACHINE_MEMORY_UPDATED', title: 'Machine Intelligence updated', summary: 'Route42 created Machine Memory version 4.', resource: { type: 'MACHINE_MEMORY', id: 'mm_local_nhvf' }, metadata: { memory_version: 4 } },
      { activity_id: 'activity_document_manual', occurred_at: '2026-08-20T12:01:00Z', category: 'DOCUMENTS', event_type: 'DOCUMENT_ADDED', title: 'Document added', summary: 'NHVf OEM Manual.pdf was added to this machine.', resource: { type: 'DOCUMENT', id: 'doc_nhvf_manual' } },
      { activity_id: 'activity_machine_created', occurred_at: '2026-08-19T09:00:00Z', category: 'MACHINE', event_type: 'MACHINE_CREATED', title: 'Machine added', summary: `${machine.machine_name} was added to Route42.`, resource: { type: 'MACHINE', id: machineId } },
      ...conversations.filter((item) => item.machine_id === machineId && item.conversation_type === 'GENERAL_MACHINE_CONVERSATION' && item.turn_count > 0).map((item) => ({ activity_id: `activity_conversation_${item.conversation_id}`, occurred_at: item.created_at, category: 'CONVERSATIONS', event_type: 'CONVERSATION_STARTED', title: 'Conversation started', summary: item.title ?? 'Machine conversation', resource: { type: 'CONVERSATION', id: item.conversation_id } })),
      ...investigations.filter((item) => item.machine_id === machineId && item.status !== 'ARCHIVED').map((item) => ({ activity_id: `activity_investigation_${item.investigation_id}`, occurred_at: item.updated_at, category: 'INVESTIGATIONS', event_type: 'INVESTIGATION_UPDATED', title: item.source_conversation_id ? 'Investigation created from conversation' : 'Investigation started', summary: item.title, resource: { type: 'INVESTIGATION', id: item.investigation_id } })),
    ].sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
    const category = (url.searchParams.get('category') ?? 'ALL').toLocaleUpperCase();
    const filtered = category === 'ALL' ? values : values.filter((item) => item.category === category);
    return { schema_version: '1.0', machine_id: machineId, activity: structuredClone(filtered), next_cursor: null, request_id: 'req_local_activity' } as T;
  }

  const machineWorkspace = url.pathname.match(/^\/v1\/machines\/([^/]+)\/workspace$/);
  if (machineWorkspace && method === 'GET') {
    const machineId = decodeURIComponent(machineWorkspace[1]);
    const machine = machines.find((item) => item.machine_id === machineId);
    if (!machine) throw new Error('Mock machine not found.');
    if (machineId !== 'mach_nhvf') throw new Error('MACHINE_MEMORY_NOT_READY');
    const activeMachineKnowledge = investigations
      .filter((item) => item.machine_id === machineId)
      .flatMap((item) => investigationMachineKnowledge.get(item.investigation_id) ?? [])
      .filter((item) => item.status === 'ACTIVE');
    return {
      schema_version: '1.1', workspace_status: 'READY',
      machine: { ...structuredClone(machine) },
      machine_memory: {
        machine_memory_id: 'mm_local_nhvf', memory_version: 4, status: 'ACTIVE',
        summary: 'Available maintenance evidence shows recurring coupling wear, bearing lubrication concerns, mechanical-seal degradation, and repeated alignment-related maintenance on the NHVf Pump. Route42 also sees a persistent documentation gap around the underlying cause of repeated bearing-oil contamination.',
        updated_at: '2026-08-23T12:00:00Z', source_snapshot_id: 'snap_local_4',
        size_monitoring: { artifact_size_bytes: 76525, dynamodb_size_status: 'OK' },
      },
      current_understanding: {
        document_profile: { active_document_count: 1, date_coverage: { start: '2016-08-02', end: '2026-08-22' }, document_types: { WORK_ORDER_HISTORY: 1 } },
        evidence_profile: { active_fact_count: 84, active_knowledge_chunk_count: 61, commonly_referenced_topics: ['mechanical seal', 'bearing', 'coupling', 'alignment'] },
        recurring_themes: [],
        insights: [
          {
            insight_id: 'ins_local_coupling', title: 'Flexible coupling insert deterioration recurs in maintenance history', type: 'RECURRING_COMPONENT_CONCERN', statement: 'Flexible coupling insert cracking and deterioration appears repeatedly in the available maintenance history.', evidence_strength: 'STRONG', why_it_matters: 'Repeated coupling deterioration can be a symptom of persistent alignment, loading, or installation conditions that deserve verification.', limitations: ['The current document set does not establish a single causal mechanism.'], display_priority: 1, supporting_document_ids: ['doc_nhvf_manual'], supporting_fact_ids: ['fact_1', 'fact_2'], supporting_knowledge_chunk_ids: ['kc_1', 'kc_2'], supporting_relationship_ids: ['rel_local_alignment'],
          },
          {
            insight_id: 'ins_local_seal', title: 'Mechanical-seal wear and leakage are documented concerns', type: 'SUPPORTED_MAINTENANCE_OBSERVATION', statement: 'Mechanical-seal wear and leakage appear in the available maintenance record, including corrective replacement activity.', evidence_strength: 'MODERATE', why_it_matters: 'Seal degradation can create shutdown risk and may be useful to examine alongside alignment and operating-condition evidence.', limitations: ['Only the currently available grounded documents are represented.'], display_priority: 2, supporting_document_ids: ['doc_nhvf_manual'], supporting_fact_ids: ['fact_3'], supporting_knowledge_chunk_ids: ['kc_3'], supporting_relationship_ids: [],
          },
        ],
        relationships: [
          {
            relationship_id: 'rel_local_alignment', title: 'Alignment-related maintenance appears alongside coupling deterioration', type: 'CONDITION_EVENT_SEQUENCE', statement: 'Alignment correction and coupling insert deterioration appear repeatedly in the same maintenance history. The available evidence supports a relationship, but does not establish that alignment caused each coupling event.', evidence_strength: 'MODERATE', related_components: ['coupling', 'shaft alignment'], uncertainty_notes: 'The available record does not establish a controlled causal test.', supporting_evidence: { document_ids: ['doc_nhvf_manual'], fact_ids: ['fact_1', 'fact_4'], knowledge_chunk_ids: ['kc_1'], document_summary_ids: [] }, conflicting_evidence: { document_ids: [], fact_ids: [], knowledge_chunk_ids: [], document_summary_ids: [] },
          },
        ],
        documentation_gaps: [
          { gap_id: 'gap_local_moisture', gap_type: 'MISSING_ROOT_CAUSE_EVIDENCE', statement: 'The available records do not establish the source of recurring moisture or discoloration observed in bearing lubrication.', limitations: ['Additional inspection or environmental evidence may exist outside the supplied documents.'], supporting_document_ids: ['doc_nhvf_manual'], supporting_relationship_ids: [] },
        ],
        known_conflicts: [],
        investigation_knowledge: activeMachineKnowledge.map((item) => ({
          record_id: item.record_id,
          knowledge_id: item.knowledge_id,
          knowledge_type: item.knowledge_type,
          statement: item.statement,
          investigation_id: item.investigation_id,
          closure_timestamp: item.closure_timestamp,
          reasoning_state_version: item.reasoning_state_version,
          authority: item.authority,
          supporting_evidence: structuredClone(item.supporting_evidence),
        })),
      },
      documents: [{ document_id: 'doc_nhvf_manual', filename: 'NHVf OEM Manual.pdf', file_extension: 'pdf', processing_status: 'READY_FOR_PRESENTATION_AND_QUESTIONS', lifecycle_status: 'ACTIVE', created_at: '2026-08-20T12:00:00Z', uploaded_at: '2026-08-20T12:01:00Z' }],
      recent_documents: [{ document_id: 'doc_nhvf_manual', filename: 'NHVf OEM Manual.pdf', file_extension: 'pdf', processing_status: 'READY_FOR_PRESENTATION_AND_QUESTIONS', lifecycle_status: 'ACTIVE', created_at: '2026-08-20T12:00:00Z', uploaded_at: '2026-08-20T12:01:00Z' }],
      timeline: [], investigations: structuredClone(investigations.filter((item) => item.machine_id === machineId && item.status !== 'ARCHIVED')),
      counts: { documents: 1, facts: 84, knowledge_chunks: 61, relationships: 1, insights: 2, documentation_gaps: 1, known_conflicts: 0, machine_knowledge: activeMachineKnowledge.length, investigations: investigations.filter((item) => item.machine_id === machineId && item.status !== 'ARCHIVED').length },
      capabilities: { ask_machine: true, artifact_navigation: true, investigations: true, general_conversations: true, workspace_read_model: true }, request_id: 'req_local_workspace',
    } as T;
  }

  if (url.pathname === '/v1/machines' && method === 'POST') {
    const body = JSON.parse(String(init?.body ?? '{}')) as Record<string, unknown>;
    const machineName = String(body.machine_name ?? '').trim();
    const assetId = body.asset_id ? String(body.asset_id) : null;
    const duplicate = machines.find((machine) =>
      machine.machine_name.toLocaleLowerCase() === machineName.toLocaleLowerCase() ||
      (assetId && machine.asset_id?.toLocaleLowerCase() === assetId.toLocaleLowerCase()),
    );
    if (duplicate && body.duplicate_decision !== 'CREATE_ANYWAY') {
      return {
        status: 'POSSIBLE_DUPLICATE',
        possible_matches: [{ machine_id: duplicate.machine_id, machine_name: duplicate.machine_name, asset_id: duplicate.asset_id, facility_id: duplicate.facility_id }],
        allowed_actions: ['SELECT_EXISTING', 'CREATE_ANYWAY'],
        request_id: 'req_local_duplicate',
      } as T;
    }
    const machine: MockMachine = {
      machine_id: `mach_local_${Date.now()}`, machine_name: machineName, facility_id: body.facility_id ? String(body.facility_id) : null,
      asset_id: assetId, machine_type: body.machine_type ? String(body.machine_type) : null, manufacturer: body.manufacturer ? String(body.manufacturer) : null,
      model: body.model ? String(body.model) : null, location: body.location ? String(body.location) : null,
      production_area: body.production_area ? String(body.production_area) : null, production_line: body.production_line ? String(body.production_line) : null,
      status: 'ACTIVE', updated_at: new Date().toISOString(),
    };
    machines.push(machine);
    return { machine: { ...machine, customer_id: 'cust_local_route42', aliases: Array.isArray(body.aliases) ? body.aliases : [] }, workflow: { workflow_id: `wf_${Date.now()}` }, next_step: 'UPLOAD_DOCUMENTS' } as T;
  }

  const machineDocuments = url.pathname.match(/^\/v1\/machines\/([^/]+)\/documents$/);
  if (machineDocuments && method === 'GET') {
    // Keep a short deterministic delay in local mode so the E2E suite can
    // verify that the real loading state never flashes the empty state.
    await new Promise((resolve) => window.setTimeout(resolve, 300));
    const machineId = decodeURIComponent(machineDocuments[1]);
    const lifecycleStatus = String(url.searchParams.get('lifecycle_status') || 'ACTIVE').toUpperCase();
    return {
      documents: structuredClone(documents.filter((item) => item.machine_id === machineId && String(item.lifecycle_status || 'ACTIVE').toUpperCase() === lifecycleStatus)),
      request_id: 'req_local_documents',
    } as T;
  }

  if (url.pathname === '/v1/documents/upload-requests' && method === 'POST') {
    const body = JSON.parse(String(init?.body ?? '{}')) as { machine_id?: string; files?: Array<Record<string, unknown>> };
    const machineId = String(body.machine_id ?? '');
    const uploads = (body.files ?? []).map((file) => {
      const clientFileId = String(file.client_file_id ?? '');
      const originalFilename = String(file.original_filename ?? 'document');
      const size = Number(file.file_size_bytes ?? 0);
      const normalizedExtension = extensionForFilename(originalFilename);
      const maximumSize = maximumFileSizeBytes(normalizedExtension, getRuntimeConfig().documentUploadLimitsBytes);
      if (maximumSize !== null && size > maximumSize) {
        return {
          client_file_id: clientFileId,
          original_filename: originalFilename,
          status: 'REJECTED',
          error_code: 'FILE_TOO_LARGE',
          message: 'This file exceeds the configured size limit.',
          maximum_size_bytes: maximumSize,
        };
      }
      const extension = originalFilename.split('.').pop()?.toUpperCase() ?? 'FILE';
      const documentId = `doc_local_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString();
      documents.push({
        document_id: documentId, machine_id: machineId, processing_run_id: null, original_filename: originalFilename,
        file_type: extension, file_size_bytes: size, upload_status: 'AWAITING_UPLOAD', processing_status: 'NOT_STARTED', security_status: 'PENDING',
        created_at: new Date().toISOString(), uploaded_at: null, original_storage_status: null, original_location: null, content_fingerprint: null,
        duplicate_status: null, duplicate_of_document_id: null, stored_at: null, question_ready: false,
        upload_expires_at: expiresAt, upload_expired: false, abandonment_reason: null, lifecycle_status: 'ACTIVE', version: 1, review: null,
      });
      return {
        client_file_id: clientFileId, document_id: documentId, upload_token: `upl_${documentId}`, upload_method: 'PUT',
        upload_url: `mock://route42-upload/${documentId}`, required_headers: { 'Content-Type': String(file.content_type ?? 'application/octet-stream') },
        expires_at: expiresAt, status: 'AWAITING_UPLOAD',
      };
    });
    const acceptedFiles = uploads.filter((upload) => upload.status === 'AWAITING_UPLOAD').length;
    const rejectedFiles = uploads.length - acceptedFiles;
    return {
      machine_id: machineId,
      total_files: uploads.length,
      accepted_files: acceptedFiles,
      rejected_files: rejectedFiles,
      batch_status: rejectedFiles === 0 ? 'ACCEPTED' : acceptedFiles === 0 ? 'REJECTED' : 'PARTIAL_SUCCESS',
      uploads,
      request_id: 'req_local_upload',
    } as T;
  }

  const cancelDocumentUpload = url.pathname.match(/^\/v1\/documents\/([^/]+)\/upload\/cancel$/);
  if (cancelDocumentUpload && method === 'POST') {
    const documentId = decodeURIComponent(cancelDocumentUpload[1]);
    const item = documents.find((document) => document.document_id === documentId);
    if (!item) throw new Error('Mock document not found.');
    if (item.upload_status !== 'AWAITING_UPLOAD') throw new Error('The file already reached Route42, so processing has started.');
    const body = JSON.parse(String(init?.body ?? '{}')) as { reason?: string };
    item.upload_status = 'ABANDONED';
    item.lifecycle_status = 'ARCHIVED';
    item.abandonment_reason = body.reason ?? 'USER_CANCELLED';
    item.upload_expired = false;
    item.version = Number(item.version ?? 1) + 1;
    return { document: structuredClone(item), request_id: 'req_local_upload_cancel' } as T;
  }

  const reviewDocument = url.pathname.match(/^\/v1\/documents\/([^/]+)\/review-decisions$/);
  if (reviewDocument && method === 'POST') {
    const documentId = decodeURIComponent(reviewDocument[1]);
    const item = documents.find((document) => document.document_id === documentId);
    if (!item) throw new Error('Mock document not found.');
    const body = JSON.parse(String(init?.body ?? '{}')) as { decision?: 'CONFIRM' | 'REJECT' };
    const decision = body.decision;
    if (!item.review || item.review.status !== 'REQUIRED' || !decision) throw new Error('This document is not waiting for human review.');
    if (!item.review.available_decisions.includes(decision)) throw new Error('That review decision is not available for this document.');
    item.review = { ...item.review, status: 'COMPLETED', decision, reviewed_at: new Date().toISOString(), available_decisions: [] };
    if (decision === 'CONFIRM') {
      item.processing_status = item.review.type === 'MACHINE_ASSOCIATION' ? 'READY_FOR_KNOWLEDGE_EXTRACTION' : 'READY_FOR_MACHINE_ASSOCIATION';
      item.lifecycle_status = 'ACTIVE';
      window.setTimeout(() => { item.processing_status = 'KNOWLEDGE_CHUNKS_AVAILABLE'; item.question_ready = true; }, 600);
    } else {
      item.processing_status = 'BLOCKED';
      item.lifecycle_status = 'ARCHIVED';
    }
    item.version = Number(item.version ?? 1) + 1;
    return { document: structuredClone(item), request_id: 'req_local_review' } as T;
  }

  const removeDocument = url.pathname.match(/^\/v1\/documents\/([^/]+)\/remove$/);
  if (removeDocument && method === 'POST') {
    const documentId = decodeURIComponent(removeDocument[1]);
    const item = documents.find((document) => document.document_id === documentId);
    if (!item) throw new Error('Mock document not found.');
    const body = JSON.parse(String(init?.body ?? '{}')) as { reason?: string; expected_version?: number };
    const expectedVersion = Number(body.expected_version ?? 0);
    const currentVersion = Number(item.version ?? 1);
    if (expectedVersion !== currentVersion) throw new Error('The document changed before it could be removed. Refresh and try again.');
    if (item.lifecycle_status !== 'ACTIVE' || item.upload_status === 'AWAITING_UPLOAD') throw new Error('Only an active uploaded document can be removed from Machine Memory.');
    item.lifecycle_status = 'REMOVED';
    item.question_ready = false;
    item.removed_reason = String(body.reason || 'WRONG_DOCUMENT');
    item.removed_at = new Date().toISOString();
    item.removed_by = 'user_local_route42';
    item.removal_reconciliation_status = 'PENDING';
    item.removal_evidence_revision = Date.now();
    item.machine_evidence_revision = item.removal_evidence_revision;
    item.machine_evidence_reconciliation_status = 'PENDING';
    item.machine_evidence_reconciliation_stage = null;
    item.machine_evidence_reconciliation_error_code = null;
    item.machine_evidence_reconciliation_source_document_id = item.document_id;
    item.version = currentVersion + 1;
    window.setTimeout(() => {
      if (item.lifecycle_status === 'REMOVED') {
        item.removal_reconciliation_status = 'COMPLETED';
        item.machine_evidence_reconciliation_status = 'COMPLETED';
      }
    }, 900);
    return { document: structuredClone(item), request_id: 'req_local_document_remove' } as T;
  }

  const retryDocumentReconciliation = url.pathname.match(/^\/v1\/documents\/([^/]+)\/reconciliation\/retry$/);
  if (retryDocumentReconciliation && method === 'POST') {
    const documentId = decodeURIComponent(retryDocumentReconciliation[1]);
    const item = documents.find((document) => document.document_id === documentId);
    if (!item) throw new Error('Mock document not found.');
    const body = JSON.parse(String(init?.body ?? '{}')) as { expected_evidence_revision?: number };
    if (Number(body.expected_evidence_revision ?? 0) !== Number(item.machine_evidence_revision ?? 0)) throw new Error('A newer evidence reconciliation is already active.');
    if (item.lifecycle_status !== 'REMOVED' || item.removal_reconciliation_status !== 'PENDING') throw new Error('This source does not need reconciliation recovery.');
    item.machine_evidence_reconciliation_status = 'PENDING';
    item.machine_evidence_reconciliation_stage = null;
    item.machine_evidence_reconciliation_error_code = null;
    item.machine_evidence_reconciliation_retry_requested_at = new Date().toISOString();
    item.version = Number(item.version ?? 1) + 1;
    window.setTimeout(() => {
      if (item.lifecycle_status === 'REMOVED') {
        item.removal_reconciliation_status = 'COMPLETED';
        item.machine_evidence_reconciliation_status = 'COMPLETED';
      }
    }, 900);
    return { document: structuredClone(item), request_id: 'req_local_document_reconciliation_retry' } as T;
  }

  const singleDocument = url.pathname.match(/^\/v1\/documents\/([^/]+)$/);
  if (singleDocument && method === 'GET') {
    const item = documents.find((document) => document.document_id === decodeURIComponent(singleDocument[1]));
    if (!item) throw new Error('Mock document not found.');
    return { document: structuredClone(item), request_id: 'req_local_document' } as T;
  }


  const machineConversations = url.pathname.match(/^\/v1\/machines\/([^/]+)\/conversations$/);
  if (machineConversations && method === 'POST') {
    const machineId = decodeURIComponent(machineConversations[1]);
    const body = JSON.parse(String(init?.body ?? '{}')) as { title?: string };
    const now = new Date().toISOString();
    const conversation: MockConversation = {
      conversation_id: `conv_local_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      customer_id: 'cust_local_route42', machine_id: machineId, user_id: 'user_local_route42',
      conversation_type: 'GENERAL_MACHINE_CONVERSATION', title: body.title?.trim() || null, status: 'ACTIVE', turn_count: 0, created_at: now, updated_at: now,
    };
    conversations.push(conversation);
    conversationTurns.set(conversation.conversation_id, []);
    conversationScopes.set(conversation.conversation_id, []);
    return {
      schema_version: '1.0', conversation: structuredClone(conversation),
      conversation_memory: { conversation_id: conversation.conversation_id, customer_id: conversation.customer_id, machine_id: machineId, user_id: conversation.user_id, conversation_type: conversation.conversation_type, current_document_ids: [], current_filters: {}, created_at: now, last_activity_at: now },
      request_id: 'req_local_conversation_create',
    } as T;
  }
  if (machineConversations && method === 'GET') {
    const machineId = decodeURIComponent(machineConversations[1]);
    const items = conversations.filter((conversation) => conversation.machine_id === machineId && conversation.conversation_type === 'GENERAL_MACHINE_CONVERSATION').sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    return { schema_version: '1.0', machine_id: machineId, conversations: structuredClone(items), count: items.length, request_id: 'req_local_conversations' } as T;
  }

  const singleConversation = url.pathname.match(/^\/v1\/conversations\/([^/]+)$/);
  if (singleConversation && method === 'GET') {
    const conversationId = decodeURIComponent(singleConversation[1]);
    const conversation = conversations.find((item) => item.conversation_id === conversationId);
    if (!conversation) throw new Error('Mock conversation not found.');
    const scope = conversationScopes.get(conversationId) ?? [];
    const turns = conversationTurns.get(conversationId) ?? [];
    const requestedLimit = Number.parseInt(url.searchParams.get('turn_limit') ?? '', 10);
    const limit = Number.isFinite(requestedLimit) && requestedLimit > 0 ? Math.min(requestedLimit, 100) : turns.length;
    const requestedOffset = Number.parseInt(url.searchParams.get('turn_cursor') ?? '0', 10);
    const offset = Number.isFinite(requestedOffset) && requestedOffset >= 0 ? requestedOffset : 0;
    const end = Math.max(0, turns.length - offset);
    const start = Math.max(0, end - limit);
    const pageTurns = turns.slice(start, end);
    const nextTurnCursor = start > 0 ? String(offset + pageTurns.length) : null;
    return {
      schema_version: '1.0', conversation: structuredClone(conversation),
      conversation_memory: { conversation_id: conversationId, customer_id: conversation.customer_id, machine_id: conversation.machine_id, user_id: conversation.user_id, conversation_type: conversation.conversation_type, current_document_ids: [...scope], current_filters: {}, last_activity_at: conversation.updated_at },
      turns: structuredClone(pageTurns), turn_count: turns.length, next_turn_cursor: nextTurnCursor, request_id: 'req_local_conversation',
    } as T;
  }

  const conversationScope = url.pathname.match(/^\/v1\/conversations\/([^/]+)\/scope$/);
  if (conversationScope && method === 'PATCH') {
    const conversationId = decodeURIComponent(conversationScope[1]);
    const conversation = conversations.find((item) => item.conversation_id === conversationId);
    if (!conversation) throw new Error('Mock conversation not found.');
    const body = JSON.parse(String(init?.body ?? '{}')) as { document_ids?: string[] };
    const scope = Array.isArray(body.document_ids) ? body.document_ids : [];
    conversationScopes.set(conversationId, [...scope]);
    return { schema_version: '1.0', conversation_id: conversationId, machine_id: conversation.machine_id, document_ids: [...scope], request_id: 'req_local_scope' } as T;
  }

  const conversationMessages = url.pathname.match(/^\/v1\/conversations\/([^/]+)\/messages$/);
  if (conversationMessages && method === 'POST') {
    const conversationId = decodeURIComponent(conversationMessages[1]);
    const conversation = conversations.find((item) => item.conversation_id === conversationId);
    if (!conversation) throw new Error('Mock conversation not found.');
    const body = JSON.parse(String(init?.body ?? '{}')) as { question?: string; scope?: { document_ids?: string[] } };
    if (body.scope && Array.isArray(body.scope.document_ids)) conversationScopes.set(conversationId, [...body.scope.document_ids]);
    const scope = conversationScopes.get(conversationId) ?? [];
    const machineDocs = documents.filter((document) => document.machine_id === conversation.machine_id && document.lifecycle_status === 'ACTIVE' && document.question_ready);
    const evidenceDoc = (scope.length ? machineDocs.find((document) => scope.includes(document.document_id)) : machineDocs[0]) ?? null;
    if (!evidenceDoc) throw new Error('Mock machine knowledge is not ready.');
    const now = new Date().toISOString();
    const question = String(body.question ?? '').trim();
    const turn: MockTurn = {
      turn_id: `turn_local_${Date.now()}`, conversation_id: conversationId, customer_id: conversation.customer_id, machine_id: conversation.machine_id, user_id: conversation.user_id, question,
      answer: {
        answer_text: scope.length
          ? `The selected document shows grounded maintenance information relevant to “${question}”. In this local test, Route42 is intentionally limiting the answer to that document.`
          : `The available machine evidence shows recurring maintenance information relevant to “${question}”.

- **Recurring maintenance pattern** — the local evidence contains repeated maintenance activity.
- **Grounded source coverage** — this response is tied to the available machine document.

This local response verifies the production grounded-question flow.`,
        citations: [{ citation_id: 'E1', artifact_type: 'KNOWLEDGE_CHUNK', artifact_id: 'kc_local_1', document_id: evidenceDoc.document_id, label: evidenceDoc.original_filename, source_locator: { record: 'mock evidence' }, excerpt: 'Mock grounded maintenance evidence preserved with the promoted investigation.' }],
        uncertainty: 'This is local mock evidence used only for end-to-end UI verification.', conflicting_evidence: false,
        retrieved_artifacts: [{ artifact_type: 'KNOWLEDGE_CHUNK', artifact_id: 'kc_local_1', document_id: evidenceDoc.document_id }],
      }, created_at: now, interaction_type: 'ACE_QUESTION', actor_label: 'route42.local@example.com',
    };
    const turns = conversationTurns.get(conversationId) ?? [];
    turns.push(turn); conversationTurns.set(conversationId, turns); conversation.updated_at = now; conversation.turn_count += 1;
    if (conversation.investigation_id) {
      const investigationId = conversation.investigation_id;
      const investigation = investigations.find((item) => item.investigation_id === investigationId);
      const memory = investigationMemories.get(investigationId);
      const activity = investigationActivity.get(investigationId) ?? [];
      activity.push({ activity_id: `act_question_${Date.now()}`, investigation_id: investigationId, user_id: conversation.user_id, activity_type: 'QUESTION_ANSWERED', created_at: now, payload: { conversation_id: conversationId, turn_id: turn.turn_id, question, citation_count: turn.answer?.citations.length ?? 0 } });
      if (investigation && memory) {
        const firstAssessment = memory.conclusions.length === 0;
        const synthesis = {
          status: firstAssessment ? 'MATERIAL_CHANGE' : 'NO_MATERIAL_CHANGE',
          material_change: firstAssessment,
          summary: firstAssessment
            ? 'The grounded Investigation conversation surfaced evidence that changes the shared understanding. Route42 updated where things stand while preserving uncertainty.'
            : 'Route42 reviewed this grounded conversation turn. It is preserved, but it does not materially change where things stand yet.',
          changes: firstAssessment ? [
            { change_type: 'WHAT_WE_KNOW_CHANGED', message: 'The grounded conversation surfaced machine evidence relevant to the Investigation.' },
            { change_type: 'WHERE_THINGS_STAND_UPDATED', message: 'Where things stand was updated from the grounded evidence surfaced in the conversation.' },
          ] : [],
          uncertainty: turn.answer?.uncertainty ?? null,
          suggested_next_check: null,
          assessment_updated: firstAssessment,
          source_kind: 'CONVERSATION_TURN',
          source_id: turn.turn_id,
        };
        if (firstAssessment) {
          memory.conclusions.push({
            content_id: `ias_local_conversation_${Date.now()}`,
            content_type: 'FINAL_CONCLUSION',
            text: `Route42's grounded Investigation conversation surfaced recurring maintenance evidence relevant to “${question}”. The evidence is useful to the team's current understanding, but this local example does not establish a confirmed cause.`,
            created_by_user_id: 'route42-ai',
            created_at: now,
            supporting_evidence_ids: [],
          });
          memory.updated_at = now;
          investigation.updated_at = now;
        }
        activity.push({ activity_id: `act_synthesis_turn_${Date.now()}`, investigation_id: investigationId, user_id: conversation.user_id, activity_type: 'INVESTIGATION_SYNTHESIZED', created_at: now, payload: { synthesis } });
      }
      investigationActivity.set(investigationId, activity);
    }
    return { schema_version: '1.0', conversation_id: conversationId, machine_id: conversation.machine_id, turn: structuredClone(turn), retrieval: { evidence_count: 1, knowledge_chunks: 1, facts: 0, document_summaries: 0, machine_memory_items: 0, document_scope_ids: [...scope], machine_memory_available: false }, evidence: { strength: 'LIMITED', citation_count: 1, source_document_count: 1, conflicting: false }, request_id: 'req_local_question' } as T;
  }

  const machineInvestigations = url.pathname.match(/^\/v1\/machines\/([^/]+)\/investigations$/);
  if (machineInvestigations && method === 'GET') {
    const machineId = decodeURIComponent(machineInvestigations[1]);
    const items = investigations.filter((item) => item.machine_id === machineId && item.status !== 'ARCHIVED').sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    return { schema_version: '1.0', machine_id: machineId, investigations: structuredClone(items), count: items.length, request_id: 'req_local_investigations' } as T;
  }
  if (machineInvestigations && method === 'POST') {
    const machineId = decodeURIComponent(machineInvestigations[1]);
    const body = JSON.parse(String(init?.body ?? '{}')) as { title?: string; objective?: string };
    const now = new Date().toISOString();
    const investigationId = `inv_local_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const conversationId = `conv_local_inv_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const investigation: MockInvestigation = {
      investigation_id: investigationId, customer_id: 'cust_local_route42', machine_id: machineId,
      title: String(body.title ?? ''), objective: String(body.objective ?? ''), owner_user_id: 'user_local_route42', status: 'OPEN',
      participant_user_ids: ['user_local_route42'], participant_roles: { user_local_route42: 'OWNER' }, source_conversation_id: null,
      conversation_ids: [conversationId], created_at: now, updated_at: now,
    };
    const memory: MockInvestigationMemory = { investigation_id: investigationId, customer_id: investigation.customer_id, machine_id: machineId, working_hypotheses: [], findings: [], conflicting_evidence: [], rejected_hypotheses: [], open_questions: [], notes: [], conclusions: [], current_filters: {}, updated_at: now };
    const conversation: MockConversation = { conversation_id: conversationId, customer_id: investigation.customer_id, machine_id: machineId, user_id: 'user_local_route42', conversation_type: 'INVESTIGATION_CONVERSATION', investigation_id: investigationId, title: `${investigation.title} — Shared Conversation`, status: 'ACTIVE', turn_count: 0, created_at: now, updated_at: now };
    investigations.push(investigation); investigationMemories.set(investigationId, memory); investigationEvidence.set(investigationId, []); investigationContent.set(investigationId, []); investigationActivity.set(investigationId, [{ activity_id: `act_${Date.now()}`, investigation_id: investigationId, user_id: 'user_local_route42', activity_type: 'INVESTIGATION_CREATED', created_at: now, payload: { conversation_id: conversationId, objective: investigation.objective } }]);
    conversations.push(conversation); conversationTurns.set(conversationId, []); conversationScopes.set(conversationId, []);
    return { schema_version: '1.0', investigation: structuredClone(investigation), investigation_memory: structuredClone(memory), shared_conversation: structuredClone(conversation), machine_name: machines.find((item) => item.machine_id === machineId)?.machine_name ?? null, request_id: 'req_local_investigation_create' } as T;
  }

  const promoteConversationMatch = url.pathname.match(/^\/v1\/conversations\/([^/]+)\/promote$/);
  if (promoteConversationMatch && method === 'POST') {
    const sourceConversationId = decodeURIComponent(promoteConversationMatch[1]);
    const sourceConversation = conversations.find((item) => item.conversation_id === sourceConversationId);
    if (!sourceConversation) throw new Error('Mock source conversation not found.');
    const body = JSON.parse(String(init?.body ?? '{}')) as { title?: string; objective?: string; selected_turn_ids?: string[] };
    const now = new Date().toISOString();
    const investigationId = `inv_local_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const conversationId = `conv_local_inv_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const investigation: MockInvestigation = { investigation_id: investigationId, customer_id: sourceConversation.customer_id, machine_id: sourceConversation.machine_id, title: String(body.title ?? ''), objective: String(body.objective ?? ''), owner_user_id: 'user_local_route42', status: 'OPEN', participant_user_ids: ['user_local_route42'], participant_roles: { user_local_route42: 'OWNER' }, source_conversation_id: sourceConversationId, conversation_ids: [conversationId], created_at: now, updated_at: now };
    const memory: MockInvestigationMemory = { investigation_id: investigationId, customer_id: sourceConversation.customer_id, machine_id: sourceConversation.machine_id, working_hypotheses: [], findings: [], conflicting_evidence: [], rejected_hypotheses: [], open_questions: [], notes: [], conclusions: [], current_filters: {}, updated_at: now };
    const sharedConversation: MockConversation = { conversation_id: conversationId, customer_id: sourceConversation.customer_id, machine_id: sourceConversation.machine_id, user_id: 'user_local_route42', conversation_type: 'INVESTIGATION_CONVERSATION', investigation_id: investigationId, title: `${investigation.title} — Shared Conversation`, status: 'ACTIVE', turn_count: 0, created_at: now, updated_at: now };
    const sourceTurns = conversationTurns.get(sourceConversationId) ?? [];
    const selected = new Set(body.selected_turn_ids?.length ? body.selected_turn_ids : sourceTurns.map((turn) => turn.turn_id));
    const copied = sourceTurns.filter((turn) => selected.has(turn.turn_id)).map((turn) => ({ ...structuredClone(turn), turn_id: `turn_local_inv_${Date.now()}_${Math.random().toString(16).slice(2)}`, conversation_id: conversationId }));
    sharedConversation.turn_count = copied.length;
    const pinned: MockPinnedEvidence[] = [];
    for (const turn of copied) for (const artifact of turn.answer?.retrieved_artifacts ?? []) if (!pinned.some((item) => item.artifact_type === artifact.artifact_type && item.artifact_id === artifact.artifact_id)) pinned.push({ evidence_id: `ev_local_${pinned.length + 1}`, artifact_type: String(artifact.artifact_type), artifact_id: String(artifact.artifact_id), pinned_by_user_id: 'user_local_route42', pinned_at: now, note: 'Preserved from promoted conversation' });
    investigations.push(investigation); investigationMemories.set(investigationId, memory); investigationEvidence.set(investigationId, pinned); investigationContent.set(investigationId, []); investigationActivity.set(investigationId, [{ activity_id: `act_${Date.now()}`, investigation_id: investigationId, user_id: 'user_local_route42', activity_type: 'CONVERSATION_PROMOTED', created_at: now, payload: { source_conversation_id: sourceConversationId, selected_turn_count: copied.length, pinned_evidence_count: pinned.length } }]);
    conversations.push(sharedConversation); conversationTurns.set(conversationId, copied); conversationScopes.set(conversationId, []);
    return investigationDetail(investigationId) as T;
  }

  const investigationReconciliations = url.pathname.match(/^\/v1\/investigations\/([^/]+)\/reasoning\/reconciliations$/);
  if (investigationReconciliations && method === 'GET') {
    return { schema_version: '1.0', reasoning_version: null, reviewable: false, candidates: [], decisions: [] } as T;
  }
  if (investigationReconciliations && method === 'POST') {
    throw new Error('Relationship review requires a current, evidence-backed reasoning state.');
  }

  const investigationReasoningHistory = url.pathname.match(/^\/v1\/investigations\/([^/]+)\/reasoning\/history$/);
  if (investigationReasoningHistory && method === 'GET') {
    const investigationId = decodeURIComponent(investigationReasoningHistory[1]);
    if (!investigations.some((item) => item.investigation_id === investigationId)) throw new Error('Mock investigation not found.');
    return { schema_version: '1.0', investigation_id: investigationId, history: [], count: 0, request_id: 'req_local_reasoning_history' } as T;
  }

  const investigationReasoningFreshness = url.pathname.match(/^\/v1\/investigations\/([^/]+)\/reasoning\/freshness$/);
  if (investigationReasoningFreshness && method === 'GET') {
    const investigationId = decodeURIComponent(investigationReasoningFreshness[1]);
    if (!investigations.some((item) => item.investigation_id === investigationId)) throw new Error('Mock investigation not found.');
    return { schema_version: '1.0', investigation_id: investigationId, freshness: null, request_id: 'req_local_reasoning_freshness' } as T;
  }

  const investigationInquiries = url.pathname.match(/^\/v1\/investigations\/([^/]+)\/inquiries$/);
  if (investigationInquiries && method === 'GET') {
    const investigationId = decodeURIComponent(investigationInquiries[1]);
    if (!investigations.some((item) => item.investigation_id === investigationId)) throw new Error('Mock investigation not found.');
    return { schema_version: '1.0', investigation_id: investigationId, reasoning_version: null, recommended_inquiry: null, recommendation_state: null, inquiries: [], count: 0, request_id: 'req_local_inquiries' } as T;
  }

  const investigationReasoning = url.pathname.match(/^\/v1\/investigations\/([^/]+)\/reasoning$/);
  if (investigationReasoning && method === 'GET') {
    const investigationId = decodeURIComponent(investigationReasoning[1]);
    if (!investigations.some((item) => item.investigation_id === investigationId)) throw new Error('Mock investigation not found.');
    return { schema_version: '1.0', investigation_id: investigationId, reasoning_state: null, request_id: 'req_local_reasoning' } as T;
  }

  const investigationKnowledgeCandidates = url.pathname.match(/^\/v1\/investigations\/([^/]+)\/knowledge-candidates$/);
  if (investigationKnowledgeCandidates && method === 'POST') {
    const investigationId = decodeURIComponent(investigationKnowledgeCandidates[1]);
    if (!investigations.some((item) => item.investigation_id === investigationId)) throw new Error('Mock investigation not found.');
    const body = JSON.parse(String(init?.body ?? '{}')) as { closure?: { outcome?: string; what_learned?: string; what_was_done?: string; what_happened_afterward?: string } };
    const closure = body.closure ?? {};
    const candidates: Array<Record<string, unknown>> = [];
    if (closure.outcome === 'RESOLVED' && String(closure.what_learned ?? '').trim()) candidates.push({ candidate_id: 'mkc_local_learned', suggested_type: 'CONFIRMED_FINDING', allowed_types: ['CONFIRMED_FINDING', 'CONFIRMED_CAUSE', 'RULED_OUT_CAUSE'], statement: String(closure.what_learned).trim(), source_kind: 'CLOSURE_WHAT_LEARNED', source_id: 'what_learned', supporting_evidence: [] });
    if (closure.outcome === 'RESOLVED' && String(closure.what_was_done ?? '').trim()) candidates.push({ candidate_id: 'mkc_local_action', suggested_type: 'CORRECTIVE_ACTION', allowed_types: ['CORRECTIVE_ACTION'], statement: String(closure.what_was_done).trim(), source_kind: 'CLOSURE_WHAT_WAS_DONE', source_id: 'what_was_done', supporting_evidence: [] });
    if (closure.outcome === 'RESOLVED' && String(closure.what_happened_afterward ?? '').trim()) candidates.push({ candidate_id: 'mkc_local_outcome', suggested_type: 'VERIFIED_OUTCOME', allowed_types: ['VERIFIED_OUTCOME'], statement: String(closure.what_happened_afterward).trim(), source_kind: 'CLOSURE_WHAT_HAPPENED_AFTERWARD', source_id: 'what_happened_afterward', supporting_evidence: [] });
    return { schema_version: '1.0', investigation_id: investigationId, closure_outcome: String(closure.outcome ?? ''), requires_review: closure.outcome === 'RESOLVED' || closure.outcome === 'HANDED_OFF', candidates, request_id: 'req_local_knowledge_candidates' } as T;
  }

  const singleInvestigation = url.pathname.match(/^\/v1\/investigations\/([^/]+)$/);
  if (singleInvestigation && method === 'GET') return investigationDetail(decodeURIComponent(singleInvestigation[1])) as T;
  if (singleInvestigation && method === 'PATCH') {
    const investigationId = decodeURIComponent(singleInvestigation[1]);
    const item = investigations.find((value) => value.investigation_id === investigationId); if (!item) throw new Error('Mock investigation not found.');
    const body = JSON.parse(String(init?.body ?? '{}')) as { status?: MockInvestigation['status']; closure?: { outcome?: string; what_happened?: string; what_learned?: string; what_was_done?: string; what_happened_afterward?: string; follow_up?: string; machine_knowledge_reviewed?: boolean; machine_knowledge_confirmations?: Array<{ candidate_id?: string; knowledge_type?: string; statement?: string }> } };
    item.status = body.status ?? item.status; item.updated_at = new Date().toISOString();
    if (item.status === 'CLOSED' && body.closure) {
      const closure = body.closure;
      item.closure = { outcome: String(closure.outcome ?? 'UNABLE_TO_CONFIRM'), what_happened: String(closure.what_happened ?? ''), what_learned: String(closure.what_learned ?? ''), what_was_done: String(closure.what_was_done ?? ''), what_happened_afterward: String(closure.what_happened_afterward ?? ''), follow_up: String(closure.follow_up ?? ''), closed_by_user_id: 'user_local_route42', closed_at: item.updated_at };
      const confirmations = closure.machine_knowledge_confirmations ?? [];
      if (closure.machine_knowledge_reviewed && confirmations.length) {
        const records = confirmations.map((confirmation, index): MockMachineKnowledgeRecord => {
          const sourceKind = confirmation.candidate_id === 'mkc_local_action' ? 'CLOSURE_WHAT_WAS_DONE' : confirmation.candidate_id === 'mkc_local_outcome' ? 'CLOSURE_WHAT_HAPPENED_AFTERWARD' : 'CLOSURE_WHAT_LEARNED';
          const sourceText = sourceKind === 'CLOSURE_WHAT_WAS_DONE' ? String(closure.what_was_done ?? '') : sourceKind === 'CLOSURE_WHAT_HAPPENED_AFTERWARD' ? String(closure.what_happened_afterward ?? '') : String(closure.what_learned ?? '');
          return {
            record_id: `mkrec_local_${investigationId}_${index + 1}`,
            knowledge_id: `mk_local_${investigationId}_${index + 1}`,
            knowledge_type: String(confirmation.knowledge_type ?? 'CONFIRMED_FINDING'),
            statement: String(confirmation.statement ?? sourceText),
            investigation_id: investigationId,
            closure_timestamp: item.updated_at,
            confirmed_by_user_id: 'user_local_route42',
            reasoning_state_version: null,
            supporting_evidence: [],
            closure_source: { closure_outcome: String(closure.outcome ?? ''), source_kind: sourceKind, source_text: sourceText, source_id: sourceKind.toLocaleLowerCase() },
            authority: 'HUMAN_CONFIRMED_INVESTIGATION_CLOSURE',
            structuring_method: 'HUMAN_ENTERED',
            status: 'ACTIVE', version: 1,
            supersedes_record_id: null, superseded_by_record_id: null, lifecycle_changed_at: null, lifecycle_reason: null,
          };
        });
        investigationMachineKnowledge.set(investigationId, records);
      }
    }
    if (item.status === 'OPEN') {
      item.closure = null;
      const records = investigationMachineKnowledge.get(investigationId) ?? [];
      investigationMachineKnowledge.set(investigationId, records.map((record) => record.status === 'ACTIVE' ? { ...record, status: 'REVIEW_REQUIRED', lifecycle_changed_at: item.updated_at, lifecycle_reason: 'INVESTIGATION_REOPENED' } : record));
    }
    return investigationDetail(investigationId) as T;
  }

  const investigationUpdateMatch = url.pathname.match(/^\/v1\/investigations\/([^/]+)\/updates$/);
  if (investigationUpdateMatch && method === 'POST') {
    const investigationId = decodeURIComponent(investigationUpdateMatch[1]);
    const item = investigations.find((value) => value.investigation_id === investigationId);
    if (!item) throw new Error('Mock investigation not found.');
    const body = JSON.parse(String(init?.body ?? '{}')) as { text?: string };
    const text = String(body.text ?? '').trim();
    const now = new Date().toISOString();
    const conversationId = item.conversation_ids[0];
    const conversation = conversations.find((value) => value.conversation_id === conversationId);
    if (!conversation) throw new Error('Mock investigation conversation not found.');
    const turn: MockTurn = {
      turn_id: `turn_local_update_${Date.now()}`, conversation_id: conversationId, customer_id: item.customer_id, machine_id: item.machine_id, user_id: 'user_local_route42',
      question: text, answer: null, created_at: now, interaction_type: 'TEAM_UPDATE', actor_label: 'route42.local@example.com',
    };
    const turns = conversationTurns.get(conversationId) ?? []; turns.push(turn); conversationTurns.set(conversationId, turns);
    conversation.turn_count = turns.length; conversation.updated_at = now;
    const content = { content_id: `ic_local_update_${Date.now()}`, content_type: 'USER_NOTE', text, created_by_user_id: 'user_local_route42', created_at: now, supporting_evidence_ids: [] as string[] };
    const values = investigationContent.get(investigationId) ?? []; values.push(content); investigationContent.set(investigationId, values);
    const memory = investigationMemories.get(investigationId)!; memory.notes.push({ ...content }); memory.updated_at = now; item.updated_at = now;
    const activity = investigationActivity.get(investigationId) ?? [];
    activity.push({ activity_id: `act_team_update_${Date.now()}`, investigation_id: investigationId, user_id: 'user_local_route42', activity_type: 'TEAM_UPDATE_POSTED', created_at: now, payload: { conversation_id: conversationId, turn_id: turn.turn_id, content_id: content.content_id } });
    const synthesis = { status: 'NO_MATERIAL_CHANGE', material_change: false, summary: 'Route42 reviewed this team update. It is preserved in the Investigation, but it does not materially change where things stand yet.', changes: [], uncertainty: null, suggested_next_check: null, assessment_updated: false, source_kind: 'TEAM_UPDATE', source_id: turn.turn_id };
    activity.push({ activity_id: `act_synthesis_update_${Date.now()}`, investigation_id: investigationId, user_id: 'user_local_route42', activity_type: 'INVESTIGATION_SYNTHESIZED', created_at: now, payload: { synthesis } });
    investigationActivity.set(investigationId, activity);
    return { ...investigationDetail(investigationId), posted_turn: structuredClone(turn) } as T;
  }

  const investigationContentMatch = url.pathname.match(/^\/v1\/investigations\/([^/]+)\/content$/);
  if (investigationContentMatch && method === 'POST') {
    const investigationId = decodeURIComponent(investigationContentMatch[1]);
    const item = investigations.find((value) => value.investigation_id === investigationId); if (!item) throw new Error('Mock investigation not found.');
    const body = JSON.parse(String(init?.body ?? '{}')) as { content_type?: string; text?: string; supporting_evidence_ids?: string[] };
    const now = new Date().toISOString(); const content = { content_id: `ic_local_${Date.now()}`, content_type: String(body.content_type ?? 'USER_NOTE'), text: String(body.text ?? ''), created_by_user_id: 'user_local_route42', created_at: now, supporting_evidence_ids: body.supporting_evidence_ids ?? [] };
    const values = investigationContent.get(investigationId) ?? []; values.push(content); investigationContent.set(investigationId, values);
    const memory = investigationMemories.get(investigationId)!; const entry = { content_id: content.content_id, text: content.text, created_by_user_id: content.created_by_user_id, created_at: now, supporting_evidence_ids: content.supporting_evidence_ids, content_type: content.content_type };
    if (content.content_type === 'OPEN_QUESTION') memory.open_questions.push(content.text); else if (content.content_type === 'EVIDENCE_BACKED_FINDING') memory.findings.push(entry); else if (content.content_type === 'FINAL_CONCLUSION') memory.conclusions.push(entry); else if (content.content_type === 'USER_HYPOTHESIS' || content.content_type === 'AI_SUGGESTED_HYPOTHESIS') memory.working_hypotheses.push(entry); else if (content.content_type === 'CONFLICTING_EVIDENCE') memory.conflicting_evidence.push(entry); else if (content.content_type === 'USER_NOTE') memory.notes.push(entry); else if (content.content_type === 'REJECTED_HYPOTHESIS') memory.rejected_hypotheses.push(entry);
    memory.updated_at = now; item.updated_at = now;
    const activity = investigationActivity.get(investigationId) ?? [];
    activity.push({ activity_id: `act_content_${Date.now()}`, investigation_id: investigationId, user_id: 'user_local_route42', activity_type: 'CONTENT_RECORDED', created_at: now, payload: { content_id: content.content_id, content_type: content.content_type } });

    const materialChange = content.content_type !== 'OPEN_QUESTION' && content.content_type !== 'USER_NOTE';
    const synthesisSummary = materialChange
      ? content.content_type === 'USER_HYPOTHESIS'
        ? 'This adds a possible cause for the team to test. It is not confirmed, but it changes which explanations the Investigation should keep in view.'
        : content.content_type === 'EVIDENCE_BACKED_FINDING'
          ? 'This adds useful team-reported information to the Investigation and changes the current shared understanding.'
          : 'This update materially changes the Investigation and has been reflected in where things stand.'
      : 'Route42 reviewed this update. It is preserved in the Investigation, but it does not materially change where things stand yet.';
    let assessmentUpdated = false;
    const changes: Array<{ change_type: string; message: string }> = [];
    if (materialChange) {
      const assessmentText = content.content_type === 'USER_HYPOTHESIS'
        ? `The team is tracking a new possible cause: ${content.text}. It is not confirmed yet and should be tested against additional evidence before the team treats it as the cause.`
        : `The latest team update adds to the Investigation: ${content.text}. This information matters to the current understanding, but Route42 is preserving uncertainty until the team has enough evidence to support a cause and next action.`;
      const aiAssessment = { content_id: `ias_local_ai_${Date.now()}`, content_type: 'FINAL_CONCLUSION', text: assessmentText, created_by_user_id: 'route42-ai', created_at: now, supporting_evidence_ids: [] as string[] };
      memory.conclusions.push({ ...aiAssessment });
      assessmentUpdated = true;
      changes.push({ change_type: content.content_type === 'USER_HYPOTHESIS' ? 'POSSIBLE_CAUSE_STRENGTHENED' : 'WHAT_WE_KNOW_CHANGED', message: content.content_type === 'USER_HYPOTHESIS' ? 'A new possible cause is now part of the working explanation set; it remains unconfirmed.' : 'The latest team update has been added to the shared understanding.' });
      changes.push({ change_type: 'WHERE_THINGS_STAND_UPDATED', message: 'Where things stand was updated to reflect the new contribution while preserving uncertainty.' });
    }
    const synthesis = {
      status: materialChange ? 'MATERIAL_CHANGE' : 'NO_MATERIAL_CHANGE',
      material_change: materialChange,
      summary: synthesisSummary,
      changes,
      uncertainty: materialChange ? 'This local example does not treat a team contribution by itself as confirmed root cause.' : null,
      suggested_next_check: content.content_type === 'USER_HYPOTHESIS' ? 'Look for a measurement, inspection result, or evidence item that could support or weaken this possible cause.' : null,
      assessment_updated: assessmentUpdated,
      source_kind: 'CONTENT',
      source_id: content.content_id,
    };
    activity.push({ activity_id: `act_synthesis_${Date.now()}`, investigation_id: investigationId, user_id: 'user_local_route42', activity_type: 'INVESTIGATION_SYNTHESIZED', created_at: now, payload: { synthesis } });
    investigationActivity.set(investigationId, activity);
    return investigationDetail(investigationId) as T;
  }

  const machineLifecycle = url.pathname.match(/^\/v1\/machines\/([^/]+)\/(archive|restore)$/);
  if (machineLifecycle && method === 'POST') {
    const machineId = decodeURIComponent(machineLifecycle[1]);
    const action = machineLifecycle[2];
    const machine = machines.find((item) => item.machine_id === machineId);
    if (!machine) throw new Error('Mock machine not found.');
    const previousStatus = machine.status;
    const nextStatus = action === 'archive' ? 'ARCHIVED' : 'ACTIVE';
    machine.status = nextStatus;
    machine.version = Number(machine.version ?? 1) + 1;
    machine.updated_at = new Date().toISOString();
    machine.status_changed_at = machine.updated_at;
    if (nextStatus === 'ARCHIVED') machine.archived_at = machine.updated_at;
    if (nextStatus === 'ACTIVE') machine.restored_at = machine.updated_at;
    return {
      machine: structuredClone(machine),
      lifecycle: { previous_status: previousStatus, status: nextStatus, changed_at: machine.updated_at, changed_by: 'user_local_route42' },
      request_id: `req_local_machine_${action}`,
    } as T;
  }

  const selected = url.pathname.match(/^\/v1\/machines\/([^/]+)\/select$/);
  if (selected && method === 'POST') {
    const machine = machines.find((item) => item.machine_id === decodeURIComponent(selected[1]));
    if (!machine) throw new Error('Mock machine not found.');
    return { machine: { ...machine, customer_id: 'cust_local_route42', aliases: [] }, workflow: { workflow_id: `wf_${Date.now()}` }, next_step: 'UPLOAD_DOCUMENTS' } as T;
  }

  throw new Error(`No Route42 mock API handler exists for ${method} ${url.pathname}`);
}



function investigationDetail(investigationId: string) {
  const investigation = investigations.find((item) => item.investigation_id === investigationId);
  if (!investigation) throw new Error('Mock investigation not found.');
  const pinned = investigationEvidence.get(investigationId) ?? [];
  const content = investigationContent.get(investigationId) ?? [];
  const activity = investigationActivity.get(investigationId) ?? [];
  const latestSynthesisActivity = [...activity].reverse().find((item) => item.activity_type === 'INVESTIGATION_SYNTHESIZED' || item.activity_type === 'INVESTIGATION_SYNTHESIS_UNAVAILABLE');
  const latestSynthesis = latestSynthesisActivity && typeof latestSynthesisActivity.payload.synthesis === 'object' && latestSynthesisActivity.payload.synthesis !== null
    ? { ...(latestSynthesisActivity.payload.synthesis as Record<string, unknown>), reviewed_at: latestSynthesisActivity.created_at, source_user_id: latestSynthesisActivity.user_id, activity_id: latestSynthesisActivity.activity_id }
    : null;
  const participantProfiles = investigation.participant_user_ids.map((userId) => {
    if (userId === 'user_local_route42') return { user_id: userId, display_name: 'Local Route42 User' };
    if (userId === 'user_local_manager') return { user_id: userId, display_name: 'Maintenance Manager' };
    return { user_id: userId, display_name: 'Team member' };
  });
  const machineKnowledge = investigationMachineKnowledge.get(investigationId) ?? [];
  return { schema_version: '1.1', investigation: structuredClone(investigation), investigation_memory: structuredClone(investigationMemories.get(investigationId) ?? null), pinned_evidence: structuredClone(pinned), content: structuredClone(content), activity: structuredClone(activity), machine_knowledge: structuredClone(machineKnowledge), participant_profiles: structuredClone(participantProfiles), latest_synthesis: structuredClone(latestSynthesis), access: { current_user_role: investigation.participant_roles.user_local_route42 ?? 'CONTRIBUTOR', is_participant: investigation.participant_user_ids.includes('user_local_route42'), can_contribute: true, can_manage: investigation.owner_user_id === 'user_local_route42' }, counts: { evidence: pinned.length, content: content.length, activity: activity.length, conversations: investigation.conversation_ids.length, machine_knowledge: machineKnowledge.length, active_machine_knowledge: machineKnowledge.filter((item) => item.status === 'ACTIVE').length }, request_id: 'req_local_investigation' };
}

export function completeMockDocumentUpload(documentId: string) {
  const document = documents.find((item) => item.document_id === documentId);
  if (!document || document.lifecycle_status !== 'ACTIVE' || document.upload_status === 'ABANDONED') return;
  document.upload_status = 'UPLOADED';
  document.processing_status = 'READY_FOR_STORAGE';
  document.security_status = 'PASSED';
  document.uploaded_at = new Date().toISOString();
  window.setTimeout(() => {
    document.processing_status = 'READY_FOR_PARSING';
    document.original_storage_status = 'STORED';
    document.stored_at = new Date().toISOString();
  }, 250);
  window.setTimeout(() => {
    document.processing_status = 'KNOWLEDGE_CHUNKS_AVAILABLE';
    document.question_ready = true;
  }, 750);
}
